import { Button, Popover, Select, Spin, Tabs } from 'antd';
import TabPane from 'antd/es/tabs/TabPane';
import BillingPayoutsTable from './components/BillingPayoutsTable';
import PayLaterBanner from 'components/PayLaterBanner';
import React from 'react';
import MarketingInvestment from './components/MarketingInvestment';
import CredentialingDeposits from './components/CredentialingDeposits';
import InsuranceBillingTab from './components/InsuranceBillingTab';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import ReimbursementsSetupModal from './components/ReimbursementsSetupModal';
import PaymentSettingModal from './components/PaymentSettingModal';
import PaymentFailBanner from 'components/PaymentFailBanner';
import useInsuranceForecastSummary from 'hooks/useInsuranceForecastSummary';
import useInsuranceBillingStatus from 'hooks/useInsuranceBillingStatus';
import AIChat from 'components/AIChat';
import { EAIChatPageId } from 'types/ai';
import PriceLabel from 'components/PriceLabel';
import PayLaterTips from 'components/PayLaterTips';
import { getHasClosePayLaterBannerInDashboard, getHasClosePayLaterTipsInFinancials } from 'utils/localstore';
import useCurrentUser from 'hooks/useCurrentUser';
import { canShowGroupPracticeView } from 'utils/common';
import { ERoleCode } from 'types/enumItems';

const { Option } = Select;

const FinancialsPage = () => {
    const [selectedPeriod, setSelectedPeriod] = React.useState(1);
    const [showReimbursementsSetupModal, setShowReimbursementsSetupModal] = React.useState(false);
    const [showSettingModal, setShowSettingModal] = React.useState(false);
    const [activeView, setActiveView] = React.useState<'practice' | 'my'>('practice');
    const isPractice = activeView === 'practice';
    const [insuranceForecastSummary, insuranceForecastSummaryLoading, fetchInsuranceForecastSummary] =
        useInsuranceForecastSummary(selectedPeriod, isPractice);
    const [activeTab, setActiveTab] = React.useState<
        'payouts' | 'marketing-investment' | 'credentialing' | 'insurance-billing'
    >('payouts');

    const [currentUser] = useCurrentUser();
    // Group-practice members don't manage their own payouts (payouts are handled at the
    // practice level), so the Payouts tab is hidden in My Financials for everyone in a
    // group practice — owner, staff, and providers alike.
    const isGroupMember = !!currentUser?.groupPractice;
    const showPayoutsTab = !(activeView === 'my' && isGroupMember);
    // Only the group practice owner can manage payment settings / estimation. In My Financials,
    // hide the Settings entry and the "Update estimation" action for non-owner group members.
    const isGroupOwner = currentUser?.roleCode === ERoleCode.GROUP_OWNER;
    const hideOwnerActions = activeView === 'my' && isGroupMember && !isGroupOwner;

    // Insurance billing tab（白名单已移除）；tab 内按是否加入 billing program 互斥展示面板。
    // 可见性矩阵：solo 两视图都显示（个人视图，practice=自己）；group 下 Practice View 仅
    // owner/admin（practice 聚合发票），My Financials 仅 owner（个人发票）；member 任何视图都没有——
    // eligibility 计费按 provider 拆账但统一扣 owner 的卡，billing 是 owner 的事。
    // enrolled 的 group owner 在 My Financials 下整个 tab 隐藏（2026-09-16 拍板）：
    // check/advanced search 免费后个人视图没有任何账单，fee 归 Practice View
    const [ibStatus] = useInsuranceBillingStatus();
    // 与 InsuranceBillingTab / InsuranceBillingPage 的 enrolled 判定一致
    const ibEnrolled = ibStatus?.canBind
        ? !!ibStatus?.bound && ibStatus?.onboardingCompleted === true
        : !!ibStatus?.canView;
    const showInsuranceTab =
        (!isGroupMember || (isPractice ? canShowGroupPracticeView(currentUser) : isGroupOwner)) &&
        !(!isPractice && isGroupMember && ibEnrolled);

    React.useEffect(() => {
        if ((!showPayoutsTab && activeTab === 'payouts') || (!showInsuranceTab && activeTab === 'insurance-billing')) {
            setActiveTab('marketing-investment');
        }
    }, [showPayoutsTab, showInsuranceTab, activeTab]);

    const handleTabChange = (key: string) => {
        setActiveTab(key as 'payouts' | 'marketing-investment' | 'credentialing' | 'insurance-billing');
    };

    const VIEW_TABS: { key: 'practice' | 'my'; label: string }[] = [
        { key: 'practice', label: 'Practice View' },
        { key: 'my', label: 'My Financials' },
    ];

    const handleClickSettings = () => {
        setShowSettingModal(true);
    };

    const handlePeriodChange = (value: number) => {
        setSelectedPeriod(value);
    };

    const isOptionDisabled = (weeks: number): boolean => {
        return false;
        // if (!budgetInfo.createdAt) return false;

        // // 从创建时间到现在经过了多少周
        // const createdDate = new Date(budgetInfo.createdAt);
        // const now = new Date();

        // const timeDifference = now.getTime() - createdDate.getTime();
        // const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;

        // const weeksSinceCreated = Math.ceil(timeDifference / oneWeekInMs);
        // return weeksSinceCreated < weeks;
    };

    // return (
    //     <FinancialsLandingPage
    //         onPaymentInfoChange={() => {
    //             setShowSettingModal(true);
    //         }}
    //     />
    // );

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <div className={s.left}>
                    <div className={s.title}>Financials</div>
                    <div className={s.des}>
                        View and manage your practice&apos;s financial activity across all providers.
                    </div>
                </div>
                <div className={s.right}>
                    {!hideOwnerActions && (
                        <Button className={s.btn} onClick={handleClickSettings}>
                            Settings
                        </Button>
                    )}
                </div>
            </div>
            <div className={s.content}>
                <div className={s.viewTabs}>
                    {VIEW_TABS.map((tab) => (
                        <button
                            type="button"
                            key={tab.key}
                            className={`${s.viewTab} ${activeView === tab.key ? s.viewTabActive : ''}`}
                            onClick={() => setActiveView(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <PaymentFailBanner />
                {/* <PayLaterBanner /> */}
                {getHasClosePayLaterBannerInDashboard() && <PayLaterTips />}
                {insuranceForecastSummary ? (
                    <div className={s.overview}>
                        <div className={s.overviewHeader}>
                            <div className={s.oTitle}>Overview</div>
                            <div className={s.controls}>
                                {/* <Select
                                        value={selectedPeriod}
                                        onChange={(value) => handlePeriodChange(value)}
                                        className={s.select}
                                        dropdownStyle={{ minWidth: '160px' }}
                                    >
                                        <Option value={1}>Last 1 week</Option>
                                        <Option value={2} disabled={isOptionDisabled(2)}>Last 2 weeks</Option>
                                        <Option value={4} disabled={isOptionDisabled(4)}>Last 4 weeks</Option>
                                        <Option value={8} disabled={isOptionDisabled(8)}>Last 8 weeks</Option>
                                        <Option value={12} disabled={isOptionDisabled(12)}>Last 12 weeks</Option>
                                        <Option value={0}>Since you started</Option>
                                    </Select> */}
                                <Select
                                    value={selectedPeriod}
                                    onChange={(value) => handlePeriodChange(value)}
                                    className={s.select}
                                    dropdownStyle={{ minWidth: '160px' }}
                                >
                                    <Option value={1}>Last 1 month</Option>
                                    <Option value={2}>Last 2 months</Option>
                                    <Option value={3}>Last 3 months</Option>
                                    <Option value={4}>Last 4 months</Option>
                                    <Option value={5}>Last 5 months</Option>
                                    <Option value={6}>Last 6 months</Option>
                                    {/* <Option value={0}>Since you started</Option> */}
                                </Select>
                            </div>
                        </div>
                        <div className={s.overviewSection}>
                            <div className={s.overviewSectionItem}>
                                <div className={s.h}>
                                    <span>Total estimated earnings</span>
                                    <Popover
                                        trigger="hover"
                                        content={
                                            <div className={s.pop}>
                                                The total includes actual payouts received from Klarity and projected
                                                revenue from upcoming insurance reimbursements.
                                            </div>
                                        }
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                                stroke="#4B5563"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </Popover>
                                </div>
                                <div className={s.priceWrap}>
                                    <Spin spinning={insuranceForecastSummaryLoading} wrapperClassName={s.price}>
                                        <PriceLabel value={insuranceForecastSummary?.totalEstimatedEarnings} />
                                    </Spin>
                                    {insuranceForecastSummary?.totalPayoutsReceived !== null &&
                                        insuranceForecastSummary?.totalPayoutsReceived !== undefined && (
                                            <span className={s.priceDesc}>
                                                (includes ${insuranceForecastSummary?.totalPayoutsReceived} collected
                                                from Klarity)
                                            </span>
                                        )}
                                </div>
                                {!hideOwnerActions && (
                                    <div className={s.actions}>
                                        <span
                                            onClick={() => {
                                                setShowReimbursementsSetupModal(true);
                                            }}
                                        >
                                            Update estimation
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className={s.overviewSectionItem}>
                                <div className={s.h}>
                                    <span>Outstanding balance</span>
                                    <Popover
                                        trigger="hover"
                                        content={
                                            <div className={s.pop}>
                                                The total unpaid charges from your active campaigns and services. You
                                                can pay the balance anytime to keep your account in good standing.
                                            </div>
                                        }
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                                stroke="#4B5563"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </Popover>
                                </div>
                                <div className={s.priceWrap}>
                                    <Spin spinning={insuranceForecastSummaryLoading} wrapperClassName={s.price}>
                                        <PriceLabel value={insuranceForecastSummary?.outstandingBalance} />
                                    </Spin>
                                    <span className={s.priceDesc} />
                                </div>
                                {/* <div className={s.actions}>
                                        <span>Pay now</span>
                                        <span>Pay later</span>
                                    </div> */}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={s.oLoading}>
                        <Spin spinning />
                    </div>
                )}
                <Tabs activeKey={activeTab} onChange={handleTabChange}>
                    {showPayoutsTab && (
                        <TabPane tab="Payouts" key="payouts">
                            <BillingPayoutsTable key={activeView} isPractice={isPractice} />
                        </TabPane>
                    )}
                    <TabPane tab="Marketing investment" key="marketing-investment">
                        <MarketingInvestment key={activeView} isPractice={isPractice} />
                    </TabPane>
                    <TabPane tab="Credentialing" key="credentialing">
                        <CredentialingDeposits key={activeView} isPractice={isPractice} />
                    </TabPane>
                    {showInsuranceTab && (
                        <TabPane tab="Insurance billing" key="insurance-billing">
                            {/* 与 Credentialing 同模式：一级 Practice View / My Financials 决定视图，key 强制重挂 */}
                            <InsuranceBillingTab key={activeView} isPractice={isPractice} />
                        </TabPane>
                    )}
                </Tabs>
            </div>
            <ReimbursementsSetupModal
                open={showReimbursementsSetupModal}
                onCancel={() => setShowReimbursementsSetupModal(false)}
                onSuccess={() => setShowReimbursementsSetupModal(false)}
            />
            <PaymentSettingModal
                open={showSettingModal}
                onClose={() => setShowSettingModal(false)}
                onSuccess={() => setShowSettingModal(false)}
            />
            <div className={commonS.chatWrap}>
                <AIChat pageId={EAIChatPageId.PROVIDER_FINANCIAL} defaultShow={false} makeFix={false} />
            </div>
        </div>
    );
};

export default FinancialsPage;
